import { action, observable } from "mobx";

import apiCaller from "@client/utils/ApiCaller";
import { Customer } from "@typings/Customer";
import { LicensorWithRole } from "@typings/Licensor";
import { Partner, PartnerInput } from "@typings/Partner";
import { Licence } from "@typings/Licence";

class PartnerStore {
  @observable loading = false;
  @observable updating = false;
  @observable partners: Partner[] = [];
  @observable licensors: LicensorWithRole[] = [];
  @observable customers: Customer[] = [];
  @observable licences: Licence[] = [];

  @action async load(opts?: {
    forceShowLoading?: boolean;
    hideLoading?: boolean;
  }): Promise<void> {
    this.loading = this.partners.length === 0;

    if (opts) {
      if (opts.hideLoading) this.loading = false;
      if (opts.forceShowLoading) this.loading = true;
    }
    try {
      const partners: Partner[] = await apiCaller.send({ url: "/partners" });
      this.loadSuccess(partners);
    } catch (err) {
      this.loadFailed();
    }
  }

  @action loadSuccess(partners: Partner[]) {
    this.loading = false;
    this.partners = partners;
  }

  @action loadFailed() {
    this.loading = false;
  }

  @action async loadLicensors(partnerId: string) {
    const licensors: LicensorWithRole[] = await apiCaller.send({
      url: `/licensors?partnerId=${partnerId}&includeRole=true&includeEnabled=true`,
      cancelId: `partner-${partnerId}`,
    });
    this.loadLicensorsSuccess(licensors);
  }

  @action loadLicensorsSuccess(licensors: LicensorWithRole[]) {
    this.licensors = licensors;
  }

  @action clearLicensors() {
    this.licensors = [];
  }

  @action async loadCustomers(partnerId: string) {
    const customers: Customer[] = await apiCaller.send({
      url: `/customers?partnerId=${partnerId}`,
      cancelId: `partner-${partnerId}`,
    });
    this.loadCustomersSuccess(customers);
  }

  @action loadCustomersSuccess(customers: Customer[]) {
    this.customers = customers;
  }

  @action clearCustomers() {
    this.customers = [];
  }

  @action cancelRequests(partnerId: string) {
    apiCaller.cancelRequests([`partner-${partnerId}`]);
  }

  @action async loadLicences(partnerId: string) {
    const licences: Licence[] = await apiCaller.send({
      url: `/licences?partnerId=${partnerId}`,
      cancelId: `partner-${partnerId}`,
    });
    this.loadLicencesSuccess(licences);
  }

  @action loadLicencesSuccess(licences: Licence[]) {
    this.licences = licences;
  }

  @action clearLicences() {
    this.licences = [];
  }

  @action async deleteLicensor(licensor: LicensorWithRole) {
    await apiCaller.send({
      url: `/licensors/${licensor.id}?isPartner=true`,
      method: "DELETE",
    });
    this.deleteLicensorSuccess(licensor);
  }

  @action deleteLicensorSuccess(licensor: LicensorWithRole) {
    this.licensors = this.licensors.filter(l => l.id !== licensor.id);
  }

  @action async disableLicensor(licensor: LicensorWithRole) {
    const enabled: boolean = await apiCaller.send({
      url: `/licensors/${licensor.id}/enabled`,
      method: "PUT",
      data: {
        enabled: !licensor.enabled,
      },
    });
    this.disableLicensorSuccess(licensor, enabled);
  }

  @action disableLicensorSuccess(licensor: LicensorWithRole, enabled: boolean) {
    this.licensors = this.licensors.map(l => {
      if (l.id === licensor.id) {
        return {
          ...l,
          enabled,
        };
      }
      return l;
    });
  }

  @action async add(partner: PartnerInput): Promise<Partner | null> {
    this.updating = true;
    try {
      const addedPartner: Partner = await apiCaller.send({
        url: "/partners",
        method: "POST",
        data: partner,
      });
      this.addSuccess(addedPartner);
      return addedPartner;
    } catch (err) {
      this.addFailed();
      return null;
    }
  }

  @action addSuccess(partner: Partner) {
    this.updating = false;
    const partners = [...this.partners, partner];
    partners.sort((a, b) => a.name.localeCompare(b.name));
    this.partners = [...partners];
  }

  @action addFailed() {
    this.updating = false;
  }

  @action async update(
    partnerId: string,
    partner: PartnerInput,
  ): Promise<Partner | null> {
    this.updating = true;
    try {
      const updatedPartner: Partner = await apiCaller.send({
        url: `/partners/${partnerId}`,
        method: "PUT",
        data: partner,
      });
      this.updateSuccess(updatedPartner);
      return updatedPartner;
    } catch (err) {
      this.updateFailed();
      return null;
    }
  }

  @action updateSuccess(partner: Partner) {
    const partners = this.partners.filter(c => c.id !== partner.id);
    partners.push({ ...partner });
    partners.sort((a, b) => a.name.localeCompare(b.name));
    this.partners = [...partners];
    this.updating = false;
  }

  @action updateFailed() {
    this.updating = false;
  }

  async delete(partnerId: string) {
    await apiCaller.send({
      url: `/partners/${partnerId}`,
      method: "DELETE",
    });
    this.deleteSuccess(partnerId);
  }

  @action deleteSuccess(partnerId: string) {
    this.partners = this.partners.filter(partner => partner.id !== partnerId);
  }
}

export default new PartnerStore();
